import type { AdminMoneyHealthListDto, AdminMoneyHoldDto } from '@carcheck/shared';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { HoldRow } from './HoldRow';
import { ClosingIcon } from './icons';
import { SectionCard } from './SectionCard';
import { SectionMeta } from './SectionMeta';
import { countText, thresholdText, truncationText } from '../_lib/moneyHealthText';

/**
 * «Окно холда закрывается» — holds the automation still owns, whose
 * `holdExpiresAt` falls inside `thresholds.holdClosingLeadMs`.
 *
 * **Not an alert, on purpose.** These rows are being worked by the sweep; the
 * operator watches the clock, they are not a to-do list. The lead time in the
 * subtitle is interpolated from the wire, never hardcoded — the threshold is on
 * the payload precisely so moving the rule is one deploy.
 */
export const ClosingHoldsSection = ({
  list,
  holdClosingLeadMs,
  now,
}: {
  list: AdminMoneyHealthListDto<AdminMoneyHoldDto>;
  holdClosingLeadMs: number;
  now?: number | undefined;
}): ReactElement => {
  const { t } = useTranslation();
  const count = countText(list);
  const total = t(count.key, { ...count.values });
  const truncation = truncationText(list, total);
  const threshold = thresholdText(holdClosingLeadMs);

  return (
    <SectionCard
      data-testid="section-closing"
      tone="neutral"
      icon={<ClosingIcon />}
      title={t('moneyHealth.closing.title')}
      subtitle={t('moneyHealth.closing.sub', {
        threshold: t(threshold.key, { ...threshold.values }),
      })}
      tag={{ label: t('moneyHealth.closing.tag'), tone: 'neutral' }}
      count={total}
      footer={truncation === null ? undefined : <SectionMeta text={truncation} />}
    >
      {list.items.map((hold) => (
        <HoldRow key={hold.paymentId} hold={hold} now={now} />
      ))}
    </SectionCard>
  );
};
